<div class="dash-shell df-create">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" [routerLink]="['/entity', entityId, 'dashboard']">Business</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" [routerLink]="['/entity', entityId, 'df']">Debtors Finance</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">Apply</span>
    </nav>
  </header>

  <!-- Product hero — frames what Debtors Finance is and what it takes to apply. -->
  <section class="df-hero">
    <div class="df-hero__main">
      <span class="df-hero__eyebrow">
        <app-icon name="trending-up" [size]="15" />
        Debtors Finance
      </span>
      <h1 class="df-hero__title">Apply for Debtors Finance</h1>
      <p class="df-hero__lead">
        Unlock cash tied up in your unpaid invoices. Tell us about your debtors book, attach a few
        supporting documents, and submit — we'll set up a facility and confirm your limit.
      </p>
    </div>
    <ul class="df-hero__facts" aria-label="What you'll need">
      <li class="df-hero__fact">
        <span class="df-hero__fact-icon"><app-icon name="wallet" [size]="18" /></span>
        <span class="df-hero__fact-text">
          <span class="df-hero__fact-label">Facility limit</span>
          You suggest, CapX confirms
        </span>
      </li>
      <li class="df-hero__fact">
        <span class="df-hero__fact-icon"><app-icon name="document" [size]="18" /></span>
        <span class="df-hero__fact-text">
          <span class="df-hero__fact-label">Documents</span>
          Statements &amp; debtors age analysis
        </span>
      </li>
      <li class="df-hero__fact">
        <span class="df-hero__fact-icon"><app-icon name="clock" [size]="18" /></span>
        <span class="df-hero__fact-text">
          <span class="df-hero__fact-label">Decision</span>
          A few business days
        </span>
      </li>
    </ul>
  </section>

  <!-- Stepper — completed steps are tappable to go back. -->
  <ol class="df-stepper" aria-label="Application steps">
    @for (s of steps; track s.id; let i = $index) {
      <li
        class="df-stepper__item"
        [class.df-stepper__item--done]="stepState(s.id) === 'done'"
        [class.df-stepper__item--active]="stepState(s.id) === 'active'"
        [attr.aria-current]="stepState(s.id) === 'active' ? 'step' : null"
      >
        <button
          type="button"
          class="df-stepper__btn"
          [disabled]="stepState(s.id) !== 'done'"
          (click)="goToStep(s.id)"
        >
          <span class="df-stepper__marker">
            @if (stepState(s.id) === 'done') {
              <app-icon name="check" [size]="14" />
            } @else {
              {{ i + 1 }}
            }
          </span>
          <span class="df-stepper__label">{{ s.label }}</span>
        </button>
      </li>
    }
  </ol>

  @if (serverError(); as error) {
    <div class="df-banner df-banner--error" role="alert">
      <app-icon name="x-circle" [size]="18" />
      <span>{{ error }}</span>
    </div>
  }

  <!-- STEP 1 — Details -->
  @if (step() === 'details') {
    <mat-card appearance="outlined" class="df-card">
      <mat-card-content>
        <div class="df-card__head">
          <h2 class="df-card__title">Tell us about the facility</h2>
          <p class="df-card__hint">
            Describe your debtors book and how you'd like to use the facility. A requested limit is
            optional — leave it blank and CapX will determine the right limit for you.
          </p>
        </div>

        <form [formGroup]="form" (ngSubmit)="createDraft()" class="df-form">
          <mat-form-field appearance="outline" class="df-form__field">
            <mat-label>Facility purpose / intent (optional)</mat-label>
            <textarea
              matInput
              formControlName="df_intent"
              rows="3"
              placeholder="e.g. We invoice large retailers on 60-day terms and want to bridge the wait for payment"
            ></textarea>
            <mat-hint>A short note helps us assess your application faster.</mat-hint>
          </mat-form-field>

          <p class="df-form__sublabel">Suggested limit (optional)</p>
          <div class="df-presets" role="group" aria-label="Suggested facility limits">
            @for (preset of LIMIT_PRESETS; track preset) {
              <button
                type="button"
                class="df-preset"
                [class.df-preset--selected]="form.controls.requested_limit.value === preset"
                (click)="pickLimit(preset)"
              >
                {{ preset | zar }}
              </button>
            }
          </div>

          <mat-form-field appearance="outline" class="df-form__field">
            <mat-label>Requested limit (optional)</mat-label>
            <span matTextPrefix class="df-form__prefix">ZAR&nbsp;</span>
            <input matInput type="number" formControlName="requested_limit" min="1" />
            <mat-hint>Leave blank if you want CapX to determine the appropriate limit.</mat-hint>
            @if (
              form.controls.requested_limit.touched && form.controls.requested_limit.hasError('min')
            ) {
              <mat-error>Enter a positive amount.</mat-error>
            }
          </mat-form-field>

          <div class="df-actions">
            <a mat-stroked-button [routerLink]="['/entity', entityId, 'dashboard']">Cancel</a>
            <button
              mat-flat-button
              color="primary"
              type="submit"
              [disabled]="isSaving()"
              data-testid="continue-btn"
            >
              @if (isSaving()) {
                <mat-spinner diameter="20" />
              } @else {
                <span>Continue</span>
                <app-icon name="arrow-right" [size]="18" />
              }
            </button>
          </div>
        </form>
      </mat-card-content>
    </mat-card>
  }

  <!-- STEP 2 — Documents -->
  @if (step() === 'documents') {
    <mat-card appearance="outlined" class="df-card">
      <mat-card-content>
        <div class="df-card__head">
          <h2 class="df-card__title">Supporting documents</h2>
          <p class="df-card__hint">
            Drop files into the blocks below — you can add several at once. We check each one as it
            uploads and give you a quick signal on whether it looks right (max 100&nbsp;MB each, PDF
            or images). You can submit without documents and add more later.
          </p>
        </div>

        <div class="df-drops">
          <app-doc-upload-panel
            [docTypes]="DOC_TYPES"
            [shortId]="draft()?.short_id ?? null"
            [initialDocs]="initialDocs()"
            [uploadFn]="uploadDoc"
            [deleteFn]="deleteDoc"
            [fetchDocsFn]="fetchDocs"
            (inFlightChange)="anyInFlight.set($event)"
            (attachedDocsChange)="attachedDocs.set($event)"
          />
        </div>

        <div class="df-actions">
          <button mat-stroked-button type="button" (click)="back()">
            <app-icon name="arrow-left" [size]="18" />
            <span>Back</span>
          </button>
          <button
            mat-flat-button
            color="primary"
            type="button"
            (click)="toReview()"
            [disabled]="anyInFlight()"
            data-testid="to-review-btn"
          >
            @if (anyInFlight()) {
              <mat-spinner diameter="20" />
              <span>Checking documents…</span>
            } @else {
              <span>Review &amp; submit</span>
              <app-icon name="arrow-right" [size]="18" />
            }
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  }

  <!-- STEP 3 — Review -->
  @if (step() === 'review') {
    <mat-card appearance="outlined" class="df-card">
      <mat-card-content>
        <div class="df-card__head">
          <h2 class="df-card__title">Review your application</h2>
          <p class="df-card__hint">
            Check the details below. Once you submit, our team will assess your application and set
            up the facility.
          </p>
        </div>

        <dl class="df-review">
          <div class="df-review__row df-review__row--amount">
            <dt>Requested limit</dt>
            <dd class="df-review__amount">
              @if (form.controls.requested_limit.value) {
                {{ form.controls.requested_limit.value | zar }}
              } @else {
                <span class="df-review__muted">CapX to determine</span>
              }
            </dd>
          </div>
          @if (form.controls.df_intent.value?.trim()) {
            <div class="df-review__row">
              <dt>Purpose / intent</dt>
              <dd>{{ form.controls.df_intent.value }}</dd>
            </div>
          }
          <div class="df-review__row">
            <dt>Documents</dt>
            <dd>
              @if (attachedDocs().length > 0) {
                <ul class="df-review__docs">
                  @for (d of attachedDocs(); track d.short_id) {
                    <li>
                      <app-icon name="file-text" [size]="15" />
                      <span>{{ d.file_name }}</span>
                    </li>
                  }
                </ul>
              } @else {
                <span class="df-review__muted">None attached — you can add documents later.</span>
              }
            </dd>
          </div>
        </dl>

        <p class="df-consent">
          <app-icon name="shield" [size]="16" />
          <span>
            By submitting, you confirm the information is accurate and consent to CapX processing
            your personal information for this application, in line with POPIA.
          </span>
        </p>

        <!-- Sign-up question since 2026-09-22; this box is the fallback when no standing consent exists. -->
        @if (showAiConsentBox()) {
          <div class="df-ai-consent">
            <app-ai-consent-checkbox [checked]="aiConsent()" (changed)="aiConsent.set($event)" />
          </div>
        }

        <div class="df-actions">
          <button mat-stroked-button type="button" (click)="back()">
            <app-icon name="arrow-left" [size]="18" />
            <span>Back</span>
          </button>
          <button
            mat-flat-button
            color="primary"
            type="button"
            (click)="submit()"
            [disabled]="isSubmitting() || !aiConsentOk()"
            data-testid="submit-btn"
          >
            @if (isSubmitting()) {
              <mat-spinner diameter="20" />
            } @else {
              <app-icon name="check" [size]="18" />
              <span>Submit application</span>
            }
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  }
</div>
